async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...init });
  if (r.status === 401) { window.location.href = '/login'; throw new Error('401'); }
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json() as Promise<T>;
}

export type DrillLevel = {
  layerId: string;
  idColumn: string;
  parentColumn?: string;
  label?: string;
};

export type DrillConfig = {
  enabled: boolean;
  levels: DrillLevel[];
};

export type Geoportal = {
  id: string; slug: string; name: string; description: string | null;
  defaultView: { center: [number, number]; zoom: number; bearing: number; pitch: number };
  baseMap: string; public: boolean;
  drillConfig?: DrillConfig | null;
};

export type Layer = {
  id: string; geoportalId: string; dataSourceId: string | null; ord: number; name: string;
  sourceSchema: string; sourceTable: string; geomColumn: string; geomSrid: number;
  geometryType: 'Point' | 'MultiPoint' | 'Line' | 'MultiLine' | 'Polygon' | 'MultiPolygon';
  attributes: string[]; sqlFilter: string | null;
  paint: Record<string, unknown>;
  popupTemplate: string | null;
  minZoom: number; maxZoom: number; visibleDefault: boolean;
  updatedAt?: string;
};

export type Table = { schema: string; table: string; geom_column: string; srid: number; geometry_type: string };

export type DataSource = {
  id: string; name: string; kind: 'internal' | 'external';
  host: string | null; port: number | null;
  databaseName: string | null; userName: string | null;
  defaultSchema: string; sslMode: 'disable' | 'require' | 'verify-full';
  createdAt: string;
};

export type User = { id: string; email: string; displayName: string | null; createdAt: string };

export const api = {
  // auth
  me:               () => req<{ user: { id: string; email: string; displayName: string | null } | null }>('/api/auth/me'),
  login:            (email: string, password: string) => req<{ user: { id: string; email: string } }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout:           () => req('/api/auth/logout', { method: 'POST' }),

  // geoportals
  listGeoportals:   () => req<{ items: Geoportal[] }>('/api/geoportals'),
  createGeoportal:  (b: Partial<Geoportal>) => req<Geoportal>('/api/geoportals', { method: 'POST', body: JSON.stringify(b) }),
  getGeoportal:     (id: string) => req<Geoportal>(`/api/geoportals/${id}`),
  updateGeoportal:  (id: string, b: Partial<Geoportal>) => req<Geoportal>(`/api/geoportals/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
  deleteGeoportal:  (id: string) => req(`/api/geoportals/${id}`, { method: 'DELETE' }),

  // datasources (descubrimiento de tablas)
  listTables:       (dataSourceId?: string) => {
    // Default = internal (aún sin parámetro). Externos via /api/data-sources/:id/probe.
    return dataSourceId
      ? req<{ ok: boolean; tables: Table[] }>(`/api/data-sources/${dataSourceId}/probe`)
      : req<{ tables: Table[] }>('/api/datasources/tables');
  },
  listColumns:      (s: string, t: string) => req<{ columns: { name: string; type: string }[] }>(`/api/datasources/columns?schema=${s}&table=${t}`),

  // layers
  listLayers:       (geoportalId: string) => req<{ items: Layer[] }>(`/api/layers?geoportalId=${geoportalId}`),
  createLayer:      (b: Partial<Layer>) => req<Layer>('/api/layers', { method: 'POST', body: JSON.stringify(b) }),
  updateLayer:      (id: string, b: Partial<Layer>) => req<Layer>(`/api/layers/${id}`, { method: 'PUT', body: JSON.stringify(b) }),
  deleteLayer:      (id: string) => req(`/api/layers/${id}`, { method: 'DELETE' }),

  // data sources externas
  listDataSources:  () => req<{ items: DataSource[] }>('/api/data-sources'),
  createDataSource: (b: Record<string, unknown>) => req<{ id: string; name: string }>('/api/data-sources', { method: 'POST', body: JSON.stringify(b) }),
  provisionInternalSchema: (b: { name: string; slug?: string }) => req<{
    id: string; name: string;
    credentials: { host: string; port: number; database: string; schema: string; username: string; password: string };
    instructions: { ssh_tunnel: string; pgadmin_with_tunnel: { host: string; port: number; database: string; username: string; password: string } };
  }>('/api/data-sources/internal-schema', { method: 'POST', body: JSON.stringify(b) }),
  deleteDataSource: (id: string) => req(`/api/data-sources/${id}`, { method: 'DELETE' }),
  probeDataSource:  (id: string) => req<{ ok: boolean; tables?: Table[]; error?: string }>(`/api/data-sources/${id}/probe`),

  // users
  listUsers:        () => req<{ items: User[] }>('/api/users'),
  createUser:       (b: { email: string; password: string; displayName?: string }) => req<User>('/api/users', { method: 'POST', body: JSON.stringify(b) }),
  resetPassword:    (id: string, password: string) => req(`/api/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ password }) }),
  deleteUser:       (id: string) => req(`/api/users/${id}`, { method: 'DELETE' }),

  // public
  publicGeoportal:  (slug: string) => req<{ geoportal: Geoportal & { drillConfig?: DrillConfig | null }; layers: Array<Layer & { tilesUrl: string }> }>(`/api/public/${slug}`),
};
